import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { caseStudies } from '../../data/caseStudies';
import '../../styles/glass.css';

const CaseStudies6 = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Transform case studies data to match the carousel format
  const cases = Object.entries(caseStudies).map(([key, study]) => ({
    id: key,
    title: study.title,
    category: study.category,
    video: study.video,
    challenge: `${study.challenges[0].description.slice(0, 100)} ...`,
    solution: `${study.workDone[0].description.slice(0, 100)} ...`,
    results: study.stats.map(stat => ({
      metric: stat.label,
      value: stat.value,
      icon: getIconForMetric(stat.label)
    })),
    testimonial: {
      text: `${study.testimonial.quote.slice(0, 100)} ...`,
      author: study.testimonial.author,
      role: study.testimonial.position
    },
    color: getGradientForCategory(study.category)
  }));

  // Helper function to get appropriate icon for each metric
  function getIconForMetric(metric) {
    const iconMap = {
      'Sales': '💰',
      'Sales Growth': '💰',
      'Acos': '🎯',
      'ROI': '💰',
      'Rating': '⭐',
      'Growth': '📈',
      'Conversion': '🎯',
      'Visibility': '📊',
      'Listing Created': '📈',
      'Monthly Revenue': '💰',
      'Product Portfolio': '📊',
      'ROAS': '🎯',
      'ACOS': '🎯',
      'Category Rank': '📊',
    };
    return iconMap[metric] || iconMap.default;
  }

  // Helper function to get gradient colors based on category
  function getGradientForCategory(category) {
    const gradientMap = {
      'Fitness Apparel': 'from-blue-600/20 via-purple-600/20 to-pink-600/20',
      'Art & Crafts': 'from-orange-600/20 via-red-600/20 to-pink-600/20',
      'default': 'from-green-600/20 via-emerald-600/20 to-teal-600/20'
    };
    return gradientMap[category] || gradientMap.default;
  }

  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setActiveIndex((current) => (current + 1) % cases.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, cases.length]);

  return (
    <section className="bg-black text-white py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-block bg-primary/20 text-primary px-6 py-2 rounded-full text-sm font-medium mb-6">
            CASE STUDIES
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            Client Success Stories
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Real results from real businesses. See how we've helped transform brands on Amazon.
          </p>
        </div>

        {/* Case Study Showcase */}
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className={`bg-gradient-to-br ${cases[activeIndex].color} rounded-3xl p-8 md:p-12 backdrop-blur-sm border border-white/10`}
            >
              <div className="grid md:grid-cols-2 gap-12">
                {/* Main Content */}
                <div>
                  <div className="text-primary mb-4">{cases[activeIndex].category}</div>
                  <h3 className="text-3xl font-bold mb-6">{cases[activeIndex].title}</h3>

                  {/* Challenge & Solution */}
                  <div className="space-y-6 mb-8">
                    <div>
                      <div className="text-lg font-semibold mb-2">The Challenge</div>
                      <p className="text-white/60">{cases[activeIndex].challenge}</p>
                    </div>
                    <div>
                      <div className="text-lg font-semibold mb-2">Our Solution</div>
                      <p className="text-white/60">{cases[activeIndex].solution}</p>
                    </div>
                  </div>

                  {/* Results Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    {cases[activeIndex].results.map((result, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-black/30 rounded-xl p-6 backdrop-blur-sm"
                      >
                        <div className="text-2xl mb-2">{result.icon}</div>
                        <div className="text-2xl font-bold text-primary mb-1">
                          {result.value}
                        </div>
                        <div className="text-sm text-white/60">
                          {result.metric}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Testimonial & Navigation */}
                <div className="flex flex-col justify-between">
                  {/* Testimonial */}
                  <div className="bg-black/30 rounded-2xl p-8 backdrop-blur-sm mb-8">
                    <div className="text-xl italic text-white/80 mb-6">
                      "{cases[activeIndex].testimonial.text}"
                    </div>
                    <div>
                      <div className="font-semibold">{cases[activeIndex].testimonial.author}</div>
                      <div className="text-primary">{cases[activeIndex].testimonial.role}</div>
                    </div>
                  </div>

                  {/* Video */}
                  {cases[activeIndex].video && (
                    <div className="glass-effect-dark rounded-2xl p-4 mb-8">
                      <div className="aspect-video w-full">
                        <iframe 
                          className="w-full h-full rounded-2xl"
                          src={cases[activeIndex].video} 
                          title={`${cases[activeIndex].title} Case Study Video`}
                          frameBorder="0" 
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                          allowFullScreen
                          onClick={() => setIsAutoPlaying(false)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex flex-col space-y-4">
                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        {cases.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              setActiveIndex(idx);
                              setIsAutoPlaying(false);
                            }}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                              activeIndex === idx ? 'bg-primary w-12' : 'bg-white/20'
                            }`}
                          />
                        ))}
                      </div>
                      <button
                        onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                        className={`p-2 rounded-full transition-colors duration-300 ${
                          isAutoPlaying ? 'bg-primary/20 text-primary' : 'bg-white/10 text-white/60'
                        }`}
                      >
                        {isAutoPlaying ? (
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <Link 
                      to={`/case-studies/${cases[activeIndex].id}`} 
                      className="w-full bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-xl font-medium transition-colors duration-300 flex items-center justify-center gap-2"
                    >
                      View Full Case Study
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies6;
